import {useContext, useEffect, useMemo, useState} from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Stack,
  Switch,
  Text,
  useToast,
} from "@chakra-ui/react";
import {FiClock, FiDownload, FiPlay, FiSearch, FiSend, FiShield, FiWifi} from "react-icons/fi";
import {AdapterContext, ConnectionContext} from "../Contexts";
import {DeviceTelemetry} from "../components/DeviceTelemetry";
import {
  formatBytes,
  formatCommandCode,
  getResponseCommand,
  parseHexPayload,
  uint16Payload,
} from "../adapters/sguaiCommands";

const sensitiveCommands = new Set([
  0x03, 0x05, 0x07, 0x0d, 0x12, 0x14, 0x17, 0x20,
  0x30, 0x31, 0x32, 0x35, 0x36, 0x52,
]);

function operationFor(definition) {
  if (definition.read !== undefined && definition.write) return "Read / Write";
  if (definition.read !== undefined) return "Read";
  if (definition.write) return "Write";
  return "Run";
}

function supportColor(support) {
  if (support === "integrated") return "green";
  if (support === "verified") return "cyan";
  if (support === "common") return "yellow";
  return "gray";
}

function safetyColor(safety) {
  if (safety === "destructive") return "red";
  if (safety === "caution") return "orange";
  return "gray";
}

function summarizeFrame(event) {
  const bytes = event.command instanceof Uint8Array
    ? event.command
    : new Uint8Array(event.command.buffer, event.command.byteOffset, event.command.byteLength);
  const command = event.direction === ">" ? bytes[5] : getResponseCommand(bytes);
  const code = command == null ? "Unknown" : formatCommandCode(command);
  if (sensitiveCommands.has(command)) return `${code} · ${bytes.length} bytes · payload redacted`;
  return `${code} · ${formatBytes(bytes)}`;
}

function ValueEditor({definition, onPayload}) {
  const editor = definition.editor;
  const firstValue = editor?.options?.[0]?.value ?? editor?.min ?? 0;
  const [value, setValue] = useState(firstValue);

  useEffect(() => {
    const nextValue = editor?.options?.[0]?.value ?? editor?.min ?? 0;
    setValue(nextValue);
    if (editor) {
      const payload = editor.type === "uint16" ? uint16Payload(nextValue) : [nextValue];
      onPayload(formatBytes(payload));
    }
  }, [definition.id, editor, onPayload]);

  if (!editor) return null;

  const update = (nextValue) => {
    const number = Number(nextValue);
    setValue(number);
    const payload = editor.type === "uint16" ? uint16Payload(number) : [number];
    onPayload(formatBytes(payload));
  };

  return (
    <FormControl>
      <FormLabel fontSize="xs" color="gray.500" textTransform="uppercase">Value</FormLabel>
      {editor.type === "options" ? (
        <Select value={value} onChange={(event) => update(event.target.value)} maxW="320px" bg="#101214" borderColor="#30343a">
          {editor.options.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </Select>
      ) : (
        <Input
          type="number"
          min={editor.min ?? 0}
          max={editor.max ?? 255}
          value={value}
          maxW="220px"
          onChange={(event) => update(event.target.value)}
        />
      )}
    </FormControl>
  );
}

function WifiProvisioning({adapter, connected}) {
  const toast = useToast();
  const [ssid, setSsid] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const provision = async () => {
    setBusy(true);
    try {
      await adapter.configureWifi(ssid, password);
      toast({title: "Wi-Fi credentials sent", status: "success", duration: 1800});
    } catch (error) {
      toast({title: "Wi-Fi provisioning failed", description: error.message, status: "error", duration: 4500});
    } finally {
      setPassword("");
      setBusy(false);
    }
  };

  return (
    <Stack spacing={4}>
      <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">Secure provisioning</Text>
      <Grid templateColumns={{base: "1fr", md: "1fr 1fr"}} gap={3}>
        <FormControl>
          <FormLabel fontSize="sm">Network name</FormLabel>
          <Input value={ssid} onChange={(event) => setSsid(event.target.value)} autoComplete="off" />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Password</FormLabel>
          <Input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" />
        </FormControl>
      </Grid>
      <Button
        alignSelf="flex-start"
        leftIcon={<FiWifi />}
        colorScheme="cyan"
        isLoading={busy}
        isDisabled={!connected || !ssid}
        onClick={provision}
      >
        Provision Wi-Fi
      </Button>
    </Stack>
  );
}

export function CommandCenter() {
  const adapter = useContext(AdapterContext);
  const connected = useContext(ConnectionContext);
  const toast = useToast();
  const deviceName = adapter.deviceName;
  const commands = useMemo(() => adapter.listCommands(deviceName), [adapter, deviceName]);
  const categories = useMemo(() => ["All", ...new Set(commands.map((item) => item.category))], [commands]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [expertMode, setExpertMode] = useState(false);
  const [selectedId, setSelectedId] = useState("battery");
  const [payload, setPayload] = useState("00");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastResponse, setLastResponse] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => adapter.onCommand((event) => {
    setHistory((current) => [{
      direction: event.direction,
      text: summarizeFrame(event),
      timestamp: new Date().toLocaleTimeString(),
    }, ...current].slice(0, 8));
  }), [adapter]);

  const visibleCommands = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return commands.filter((item) => {
      if (!expertMode && item.safety !== "safe") return false;
      if (!expertMode && connected && item.support === "unknown") return false;
      if (category !== "All" && item.category !== category) return false;
      return !normalizedQuery || `${item.name} ${item.id} ${formatCommandCode(item.command)}`.toLowerCase().includes(normalizedQuery);
    });
  }, [category, commands, connected, expertMode, query]);

  const selectedCommand = commands.find((item) => item.id === selectedId);
  const selected = visibleCommands.some((item) => item.id === selectedCommand?.id)
    ? selectedCommand
    : visibleCommands[0] ?? null;

  useEffect(() => {
    if (!selected) return;
    const defaultPayload = selected.examples?.[0]?.payload ?? selected.read ?? selected.action?.payload ?? [];
    setPayload(formatBytes(defaultPayload));
    setConfirmed(false);
    setLastResponse(adapter.getCommandResponse(selected.command));
  }, [adapter, selected]);

  const execute = async (operation) => {
    setBusy(true);
    try {
      const bytes = parseHexPayload(payload);
      let response = null;
      if (operation === "sync") {
        await adapter.syncTime();
      } else if (operation === "read") {
        response = await adapter.readCommand(selected.command, bytes, {force: expertMode});
      } else if (operation === "write") {
        response = await adapter.writeCommand(selected.command, bytes, {force: expertMode, confirmed});
      } else {
        await adapter.runCommand(selected.command, bytes, {confirmed});
      }
      setLastResponse(response ?? adapter.getCommandResponse(selected.command));
      toast({title: `${selected.name} command sent`, status: "success", duration: 1400});
    } catch (error) {
      toast({title: "Command failed", description: error.message, status: "error", duration: 4500});
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={6}>
      <Flex justify="space-between" align={{base: "flex-start", md: "center"}} gap={4} direction={{base: "column", md: "row"}}>
        <HStack spacing={2} color="#747a83" fontSize="11px" fontFamily="mono">
          <Text>{commands.length} operations</Text>
          <Text color="#3f444a">/</Text>
          <Text>{adapter.deviceName}</Text>
        </HStack>
        <HStack>
          <FiShield color={expertMode ? "#ff9d5c" : "#747a83"} />
          <Text fontSize="11px" fontWeight="700">Expert mode</Text>
          <Switch colorScheme="orange" isChecked={expertMode} onChange={(event) => setExpertMode(event.target.checked)} />
        </HStack>
      </Flex>

      <DeviceTelemetry />

      <Grid templateColumns={{base: "1fr", lg: "320px minmax(0, 1fr)"}} gap={0} borderTop="1px solid" borderBottom="1px solid" borderColor="#292d31">
        <Stack py={5} pr={{base: 0, lg: 6}} spacing={4} borderRight={{base: 0, lg: "1px solid"}} borderColor="whiteAlpha.100">
          <InputGroup>
            <InputLeftElement pointerEvents="none"><FiSearch color="#718096" /></InputLeftElement>
            <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search commands" />
          </InputGroup>
          <Select value={category} onChange={(event) => setCategory(event.target.value)} bg="#101214" borderColor="#30343a">
            {categories.map((item) => <option key={item}>{item}</option>)}
          </Select>

          <Stack spacing={0} maxH={{base: "360px", lg: "610px"}} overflowY="auto">
            {visibleCommands.map((item) => (
              <Button
                key={item.id}
                variant="ghost"
                height="auto"
                minH="58px"
                borderRadius="0"
                justifyContent="flex-start"
                px={2}
                py={2}
                borderBottom="1px solid"
                borderColor="#24282c"
                bg={selected?.id === item.id ? "#1a1d20" : "transparent"}
                _hover={{bg: "#151719"}}
                onClick={() => setSelectedId(item.id)}
              >
                <HStack width="100%" justify="space-between" minW={0}>
                  <Box minW={0} textAlign="left">
                    <Text fontSize="sm" noOfLines={1}>{item.name}</Text>
                    <Text fontSize="xs" color="gray.500">{item.category} · {operationFor(item)}</Text>
                  </Box>
                  <Badge flexShrink={0} colorScheme={supportColor(item.support)}>{formatCommandCode(item.command)}</Badge>
                </HStack>
              </Button>
            ))}
            {!visibleCommands.length && <Text color="gray.500" fontSize="sm" py={8} textAlign="center">No matching commands</Text>}
          </Stack>
        </Stack>

        {selected && (
          <Stack py={5} pl={{base: 0, lg: 7}} spacing={5} minW={0}>
            <Flex justify="space-between" align="flex-start" gap={3} wrap="wrap">
              <Box>
                <HStack spacing={2} mb={2}>
                  <Text fontSize="xl" fontWeight="bold">{selected.name}</Text>
                  <Badge colorScheme={safetyColor(selected.safety)}>{selected.safety}</Badge>
                  <Badge colorScheme={supportColor(selected.support)}>{selected.support}</Badge>
                </HStack>
                <Text color="#8a9098" fontSize="sm" maxW="680px">{selected.summary}</Text>
              </Box>
              <Text fontFamily="mono" fontSize="xl" color="#ffd84d">{formatCommandCode(selected.command)}</Text>
            </Flex>

            {selected.id === "wifi" ? <WifiProvisioning adapter={adapter} connected={connected} /> : selected.id === "time" ? (
              <Button
                alignSelf="flex-start"
                leftIcon={<FiClock />}
                colorScheme="studio"
                color="gray.900"
                isDisabled={!connected}
                isLoading={busy}
                onClick={() => execute("sync")}
              >
                Sync current time
              </Button>
            ) : (
              <>
                <ValueEditor definition={selected} onPayload={setPayload} />

                {selected.examples?.length > 0 && (
                  <HStack spacing={2} wrap="wrap">
                    {selected.examples.map((example) => (
                      <Button key={example.label} size="xs" variant="outline" onClick={() => setPayload(formatBytes(example.payload))}>
                        {example.label}
                      </Button>
                    ))}
                  </HStack>
                )}

                <FormControl>
                  <FormLabel fontSize="xs" color="gray.500" textTransform="uppercase">Payload bytes</FormLabel>
                  <Input
                    value={payload}
                    onChange={(event) => setPayload(event.target.value.toUpperCase())}
                    fontFamily="mono"
                    letterSpacing="0"
                    placeholder={selected.payload ?? "No payload"}
                    autoComplete="off"
                    bg="#101214"
                    borderColor="#30343a"
                  />
                  {selected.payload && <Text mt={2} fontSize="xs" color="gray.500">{selected.payload}</Text>}
                </FormControl>

                {selected.safety !== "safe" && (
                  <Checkbox isChecked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} colorScheme={selected.safety === "destructive" ? "red" : "orange"}>
                    Confirm this {selected.safety} device operation
                  </Checkbox>
                )}

                <HStack spacing={3} wrap="wrap">
                  {selected.read !== undefined && (
                    <Button leftIcon={<FiDownload />} variant="outline" isDisabled={!connected} isLoading={busy} onClick={() => execute("read")}>
                      Read
                    </Button>
                  )}
                  {selected.write && (
                    <Button leftIcon={<FiSend />} colorScheme="studio" color="gray.900" isDisabled={!connected || (selected.safety !== "safe" && !confirmed)} isLoading={busy} onClick={() => execute("write")}>
                      Write
                    </Button>
                  )}
                  {selected.action && (
                    <Button leftIcon={<FiPlay />} colorScheme={selected.safety === "destructive" ? "red" : "studio"} color={selected.safety === "destructive" ? undefined : "gray.900"} isDisabled={!connected || (selected.safety !== "safe" && !confirmed)} isLoading={busy} onClick={() => execute("action")}>
                      Run
                    </Button>
                  )}
                </HStack>
              </>
            )}

            <Divider borderColor="#292d31" />

            <Box>
              <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold" mb={2}>Last response</Text>
              <Box minH="74px" bg="#070809" border="1px solid" borderColor="#292d31" borderRadius="4px" p={3} overflowX="auto">
                <Text fontFamily="mono" fontSize="xs" color={lastResponse ? "cyan.200" : "gray.600"} whiteSpace="pre-wrap">
                  {lastResponse ? `${lastResponse.hex}\n\nDecoded: ${typeof lastResponse.value === "object" ? JSON.stringify(lastResponse.value) : String(lastResponse.value ?? "No value")}` : "No response captured"}
                </Text>
              </Box>
            </Box>

            <Box>
              <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold" mb={2}>Session traffic</Text>
              <Stack spacing={1} minH="72px">
                {history.map((item, index) => (
                  <HStack key={`${item.timestamp}-${index}`} fontFamily="mono" fontSize="xs" color="gray.500" align="flex-start">
                    <Text flexShrink={0}>{item.timestamp}</Text>
                    <Text color={item.direction === ">" ? "yellow.300" : "cyan.300"}>{item.direction}</Text>
                    <Text wordBreak="break-all">{item.text}</Text>
                  </HStack>
                ))}
                {!history.length && <Text fontSize="xs" color="gray.600">No command traffic in this session</Text>}
              </Stack>
            </Box>
          </Stack>
        )}
      </Grid>
    </Stack>
  );
}
